import {
  Alert,
  Card,
  Flex,
  PanelHeader,
  PanelLayout,
  PanelSidebar,
  SimpleTable,
  Switcher,
  Typography,
} from "djuno-design";
import { ReactComponent as Logo } from "./../../assets/icons/logo.svg";
import { Link } from "react-router-dom";
import { sidebarPageUrl } from "../../urls";
import { useState } from "react";
import ExpandableCard from "../../components/ExpandableCard";
import { GithubTypesLink } from "./../../urls";

const { Title, Text } = Typography;

const PanelLayoutsPage = () => {
  const [globalLoading, setGlobalLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(false);

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Flex direction="col">
          <Title level={4}>Panel Layouts</Title>
          <Title level={6}>PanelLayout, PanelSidebar, PanelHeader</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            The PanelLayout component provides a flexible layout structure for
            pages that require a sidebar and a header. It manages responsive
            behavior, including show/hide functionality for the sidebar, and
            allows for custom rendering of both the sidebar and header based on
            the layout type and current URL path.
          </Text>

          <Title level={5}>Usage</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            The PanelLayout component allows you to create a structured page
            layout with a sidebar and header. It offers a customizable interface
            where you can define how the sidebar and header are rendered.
          </Text>

          <Title level={5}>Behavior</Title>
          {/* <Text size="sm"></Text> */}
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Responsive Layout
                </Text>
                : The layout automatically adjusts between a single-column
                layout (for smaller screens) and a two-column layout (with a
                sidebar) for larger screens. The sidebar can be shown or hidden
                based on screen size and user interaction.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Sidebar
                </Text>
                : The sidebar is rendered using the renderSidebar function. It
                accepts segments (from the pathname), isShowSidebar, and type
                ('normal' or 'mini') as parameters to customize the appearance
                and functionality.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Header
                </Text>
                : The header is rendered using the renderHeader function, which
                includes handlers (handleHideSidebar and handleShowSidebar) for
                showing or hiding the sidebar in mobile layouts.
              </Text>
            </li>
          </ul>

          <Title level={5} className="mt-5">
            PanelSidebar Component
          </Title>
          <Text size="sm">
            The PanelSidebar component handles rendering the sidebar portion of
            the layout. It can be customized with its own content and styles,
            based on whether the sidebar is visible and the type of layout.
          </Text>
          <Alert
            uiType="info"
            className="mt-2 !w-auto"
            message={
              <Text size="sm">
                <Text size="sm" strong>
                  Tip:
                </Text>{" "}
                We highly recommend using{" "}
                <Typography.Link size="sm" uiType="transparent" strong>
                  <Link to={sidebarPageUrl}>Sidebar</Link>
                </Typography.Link>{" "}
                component inside the PanelSidebar (as a children)
              </Text>
            }
            showIcon
          />

          <Title level={5} className="mt-5">
            PanelHeader Component
          </Title>
          <Text size="sm">
            The PanelHeader component renders the top header bar of the layout.
            It includes functionality for showing or hiding the sidebar on
            mobile devices.
          </Text>
        </Flex>

        {/* Examples */}
        <Flex direction="col" className="w-full">
          <Title level={5} className="mt-5">
            Examples
          </Title>
          <Flex direction="col" className="gap-4 w-full">
            <ExpandableCard
              description=""
              codeViewerProps={{
                code: PanelLayoutExample,
              }}
              setting={
                <Flex items="center" className="gap-4">
                  <Flex items="center" className="gap-1">
                    <Text size="xs">global loading?</Text>
                    <Switcher
                      onChange={setGlobalLoading}
                      value={globalLoading}
                    />
                  </Flex>
                  <Flex items="center" className="gap-1">
                    <Text size="xs">content loading?</Text>
                    <Switcher
                      onChange={setContentLoading}
                      value={contentLoading}
                    />
                  </Flex>
                </Flex>
              }
            >
              <Flex direction={"col"} className="gap-3 w-full">
                <PanelLayout
                  type="mini"
                  pathname="/"
                  style={{ height: 400 }}
                  className="w-full border border-slate-300 rounded-md overflow-hidden"
                  globalLoading={globalLoading}
                  contentLoading={contentLoading}
                  renderSidebar={({ segments, ...sidebarProps }) => (
                    <PanelSidebar
                      {...sidebarProps}
                      sidebarHeader={
                        <div className="flex items-center gap-1 px-1">
                          <Logo />
                          <Text size="xs">djuno-design</Text>
                        </div>
                      }
                    >
                      <Text size="xs">sidebar</Text>
                    </PanelSidebar>
                  )}
                  renderHeader={(headerProps) => (
                    <PanelHeader {...headerProps} mobileIcon={<Logo />}>
                      <Text size="xs">header</Text>
                    </PanelHeader>
                  )}
                  enableGoToTopAfterScroll={true}
                  enableGoToTopAfterChangeRoute={true}
                >
                  <iframe
                    src="/"
                    className="w-full h-96"
                    title="djuno-design"
                  />
                </PanelLayout>
              </Flex>
            </ExpandableCard>
          </Flex>
        </Flex>

        {/* API */}
        <Flex direction="col" className="w-full mt-10">
          <Title level={5} className="">
            API
          </Title>
          <Flex direction="col" className="gap-8 w-full">
            <Card id="PanelLayout API" description="PanelLayout component">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* type */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">type</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Defines the layout type. A normal layout has a bigger
                        sidebar, while a mini layout has a smaller one.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "IPanelLayouts.ts#L21"}
                        size="xs"
                        code
                      >
                        PanelLayoutTypes
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">normal</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* pathname */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">pathname</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The current URL pathname, used to generate sidebar
                        segments.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* renderSidebar */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">renderSidebar</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        A function to render the sidebar, receiving the current
                        segments, sidebar visibility state, and layout type.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Flex direction="col" className="gap-1.5">
                        <Text
                          code
                          size="xs"
                          className="whitespace-nowrap"
                        >{`({ segments, isShowSidebar, type }) => React.ReactNode`}</Text>
                        <Typography.Text size="sm">
                          look at the{" "}
                          <Typography.Link
                            target="_blank"
                            href={GithubTypesLink + "IPanelLayouts.ts#L23"}
                            size="xs"
                            code
                          >
                            PanelLayoutProps
                          </Typography.Link>
                        </Typography.Text>
                      </Flex>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* renderHeader */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">renderHeader</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        A function to render the header, receiving handlers for
                        showing/hiding the sidebar and the current sidebar
                        visibility state.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Flex direction="col" className="gap-1.5">
                        <Text
                          code
                          size="xs"
                          className="whitespace-nowrap"
                        >{`({ handleHideSidebar, handleShowSidebar, isShowSidebar }) => React.ReactNode`}</Text>
                        <Typography.Text size="sm">
                          look at the{" "}
                          <Typography.Link
                            target="_blank"
                            href={GithubTypesLink + "IPanelLayouts.ts#L23"}
                            size="xs"
                            code
                          >
                            PanelLayoutProps
                          </Typography.Link>
                        </Typography.Text>
                      </Flex>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* className */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">className</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Additional classes to apply to the layout container.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* style */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">style</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Inline styles to apply to the layout container.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>{`React.CSSProperties`}</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* children */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">children</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The content to be displayed in the main area of the
                        layout.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>{`React.ReactNode`}</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* enableGoToTopAfterChangeRoute */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">enableGoToTopAfterChangeRoute</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Scrolling to the top after changing pathname prop
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>{`boolean`}</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">true</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* enableGoToTopAfterScroll */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">enableGoToTopAfterScroll</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Showing a button to go to the top of the page after a
                        little scrolling
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>{`boolean`}</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">true</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* loadingsContainerClassName */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">loadingsContainerClassName</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        A custom CSS class applied to the container of both
                        global and content loading overlays, allowing for custom
                        styling of the loading overlays.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>{`string`}</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* globalLoading */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">globalLoading</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Controls whether the global loading state is active.
                        When true, a loading overlay is displayed over the
                        entire layout, preventing interaction until the loading
                        completes
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>{`boolean`}</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* globalLoadingContent */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">globalLoadingContent</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Custom content to display within the global loading
                        overlay. If not provided, a default loading spinner and
                        message will be shown.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>{`React.ReactNode`}</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* contentLoading */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">contentLoading</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Controls whether the content loading state is active.
                        When true, a loading overlay is displayed over the
                        content area, preventing interaction with the page
                        content until loading completes
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>{`boolean`}</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* contentLoadingContent */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">contentLoadingContent</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Custom content to display within the content loading
                        overlay. If not provided, a default loading spinner will
                        be shown.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>{`React.ReactNode`}</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* containerRef */}
                  {/* <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">containerRef</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Provides access to the DOM element of the container
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text
                        size="xs"
                        code
                      >{`React.MutableRefObject<HTMLDivElement>`}</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row> */}
                </SimpleTable.Body>
              </SimpleTable>
            </Card>

            <Card id="PanelSidebar API" description="PanelSidebar component">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* isShowSidebar */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">isShowSidebar</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Determines whether the sidebar is visible.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* type */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">type</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Defines the width and appearance of the sidebar.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        size="xs"
                        code
                        target="_blank"
                        href={GithubTypesLink + "IPanelLayouts.ts#L21"}
                      >
                        PanelLayoutTypes
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">normal</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* sidebarHeader */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">sidebarHeader</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Optional content to render in the sidebar header
                        section.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>{`React.ReactNode`}</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* children */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">children</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The main content to display inside the sidebar.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>{`React.ReactNode`}</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>

            <Card id="PanelHeader API" description="PanelHeader component">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* handleHideSidebar */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">handleHideSidebar</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">Function to hide the sidebar</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>{`() => void`}</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* handleShowSidebar */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">handleShowSidebar</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">Function to show the sidebar.</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>{`() => void`}</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* mobileIcon */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">mobileIcon</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Optional icon to display in the mobile header for
                        sidebar interaction.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>{`React.ReactNode`}</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* children */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">children</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The content to display in the header section.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>{`React.ReactNode`}</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

const PanelLayoutExample = `<PanelLayout
  type="mini"
  pathname="/"
  style={{ height: 400 }}
  className="w-full border border-slate-300 rounded-md overflow-hidden"
  globalLoading={globalLoading}
  contentLoading={contentLoading}
  renderSidebar={({ segments, ...sidebarProps }) => (
    <PanelSidebar
      {...sidebarProps}
      sidebarHeader={
        <div className="flex items-center gap-1 px-1">
          <Logo />
          <Text size="xs">djuno-design</Text>
        </div>
      }
    >
      <Text size="xs">sidebar</Text>
    </PanelSidebar>
  )}
  renderHeader={(headerProps) => (
    <PanelHeader {...headerProps} mobileIcon={<Logo />}>
      <Text size="xs">header</Text>
    </PanelHeader>
  )}
  enableGoToTopAfterScroll={true}
  enableGoToTopAfterChangeRoute={true}
>
  <iframe
    src="/"
    className="w-full h-96"
    title="djuno-design"
  />
</PanelLayout>`;

export default PanelLayoutsPage;
